import { useState, useEffect, useCallback, useRef } from 'react';
import { useCreatePost } from '../../hooks/usePosts';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import { usePermissions } from '../../hooks/usePermissions';
import { draftService, PostDraft } from '../../services/draftService';
// import DraftsModal from '../Post/DraftsModal'; // Unused for now
import SpeechToTextButton from '../Speech/SpeechToTextButton';
import PatientPrivacyNotice from '../Post/PatientPrivacyNotice';
import { formatDistanceToNow } from 'date-fns';

interface PollOption {
  id: string;
  text: string;
}

interface CreatePostProps {
  onClose?: () => void;
}

const ALL_JOINTS = ['Shoulder', 'Hip', 'Knee', 'Foot & Ankle'];

export default function CreatePost({ onClose }: CreatePostProps) {
  // ALL hooks must be called unconditionally - no early returns allowed!
  const { canCreatePosts } = usePermissions();
  const { user, token } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const createPost = useCreatePost();
  const [userJointPreferences, setUserJointPreferences] = useState<string[]>([]);

  // Show only user's selected joints, or all if no preferences set
  const CATEGORIES = userJointPreferences.length > 0 ? userJointPreferences : ALL_JOINTS;

  // API configuration for speech service
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
  const [isExpanded, setIsExpanded] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [video, setVideo] = useState<File | null>(null);
  const [pdfDocument, setPdfDocument] = useState<File | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('Shoulder');
  const [showPoll, setShowPoll] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState<PollOption[]>([
    { id: '1', text: 'Yes' },
    { id: '2', text: 'No' },
    { id: '3', text: '' }
  ]);
  // const [showDraftsModal, setShowDraftsModal] = useState(false); // Unused for now
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [postError, setPostError] = useState<string | null>(null);
  const [showPrivacyNotice, setShowPrivacyNotice] = useState(false);
  const pollOptionRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const submitInFlight = useRef(false);

  // Fetch user's joint preferences
  useEffect(() => {
    const fetchUserPreferences = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/user/tag_preferences`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const preferences = await response.json();
          // API returns { tags: [...] }, extract the tags array
          const tagsArray = preferences.tags || preferences;

          const tagTexts: string[] = tagsArray.map((tag: any) =>
            (tag.text || tag.name || tag || '').toLowerCase()
          );

          const hasFootOrAnkle = tagTexts.some(
            (text: string) => text === 'foot' || text === 'ankle' || text === 'foot & ankle'
          );

          const formatJointName = (text: string) => {
            const normalized = text.toLowerCase();
            if (normalized === 'foot & ankle' || normalized === 'foot and ankle') {
              return 'Foot & Ankle';
            }
            return normalized.charAt(0).toUpperCase() + normalized.slice(1);
          };

          // Convert tag names to capitalized joint names
          const joints = tagTexts
            .map((text) => {
              // Skip ankle/foot here; we will add combined option below
              if (text === 'ankle' || text === 'foot' || text === 'foot & ankle') {
                return null;
              }
              return formatJointName(text);
            })
            .filter((joint: string | null): joint is string => !!joint && ALL_JOINTS.includes(joint));

          // Add "Foot & Ankle" if any related tag exists
          if (hasFootOrAnkle && !joints.includes('Foot & Ankle')) {
            joints.push('Foot & Ankle');
          }

          setUserJointPreferences(joints);
        }
      } catch (error) {
        console.log('Could not fetch user preferences:', error);
        // Fall back to showing all joints
      }
    };

    if (token) {
      fetchUserPreferences();
    }
  }, [token, API_BASE_URL]);

  // Speech-to-text handlers
  const handleSpeechTranscription = (text: string, isFinal: boolean) => {
    if (isFinal && text.trim()) {
      // Append transcribed text to existing description
      setDescription(prev => {
        const newText = prev ? `${prev} ${text.trim()}` : text.trim();
        return newText;
      });
    }
  };

  const handleSpeechError = (error: string) => {
    console.error('Speech recognition error:', error);
    alert(`Speech recognition error: ${error}`);
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitInFlight.current) return;
    submitInFlight.current = true;
    setIsSubmitting(true);
    setErrorMessage(null);
    setPostError(null);

    // Require either description or media
    if (!description.trim() && images.length === 0 && !video) {
      setPostError('Please enter a description or attach an image/video.');
      setIsSubmitting(false);
      submitInFlight.current = false;
      return;
    }

    try {
      console.log('🚀 Creating post with form data...');

      const postData: any = {
        title: title.trim(),
        description: description.trim(),
        state: 'published',
        tags: [{ text: selectedCategory }],
      };

      // Add images if selected
      if (images.length > 0) {
        postData.images = images;
        console.log('📸 Including images:', images.length, 'files');
      }

      // Add video if selected
      if (video) {
        postData.video = video;
        console.log('🎥 Including video:', video.name, video.size, 'bytes');
      }

      // Add PDF if selected
      if (pdfDocument) {
        postData.pdfDocument = pdfDocument;
        console.log('📄 Including PDF:', pdfDocument.name, pdfDocument.size, 'bytes');
      }

      // Add poll if enabled
      if (showPoll && pollQuestion.trim()) {
        postData.poll = {
          question: pollQuestion.trim(),
          options: pollOptions.map((option, index) => ({
            position: index,
            text: option.text.trim()
          })).filter(opt => opt.text.length > 0)
        };
        console.log('📊 Including poll:', postData.poll);
      }

      console.log('📤 Submitting post data:', postData);
      
      await createPost.mutateAsync(postData);
      
      // Show success toast
      showSuccess(
        'Post Created!', 
        'Your post was published successfully'
      );
      
      // Clear auto-save and current draft after successful post
      await draftService.clearAutoSave();
      if (currentDraftId && currentDraftId !== 'auto-save') {
        await draftService.deleteDraft(currentDraftId);
      }

      // Reset form
      resetForm();
      setIsExpanded(false);

      // Close the create post component if onClose is provided
      if (onClose) {
        onClose();
      }
      
    } catch (error: any) {
      console.error('Failed to create post:', error);
      
      // Extract meaningful error message
      let errorMsg = 'Failed to create post. Please try again.';
      if (error?.message) {
        if (error.message.includes('400')) {
          errorMsg = 'Invalid post data. Please check your input and try again.';
        } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMsg = 'Authentication failed. Please refresh the page and try again.';
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMsg = 'Network error. Please check your connection and try again.';
        }
      }
      
      setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
      submitInFlight.current = false;
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setImages([]);
    setVideo(null);
    setPdfDocument(null);
    // Keep current selected category or use first preference
    setSelectedCategory(prev => {
      if (userJointPreferences.length > 0 && userJointPreferences.includes(prev)) {
        return prev;
      }
      return userJointPreferences.length > 0 ? userJointPreferences[0] : CATEGORIES[0];
    });
    setShowPoll(false);
    setPollQuestion('');
    setPollOptions([{ id: '1', text: 'Yes' }, { id: '2', text: 'No' }, { id: '3', text: '' }]);
    setIsExpanded(false);
    setCurrentDraftId(null);
    setLastAutoSave(null);
  };

  // Auto-save functionality
  const autoSave = useCallback(() => {
    if (title.trim() || description.trim() || showPoll) {
      draftService.autoSave({
        title,
        description,
        selectedCategory,
        pollQuestion,
        pollOptions,
        showPoll
      });
      setLastAutoSave(new Date());
    }
  }, [title, description, selectedCategory, pollQuestion, pollOptions, showPoll]);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (isExpanded) {
      const interval = setInterval(autoSave, 30000);
      return () => clearInterval(interval);
    }
  }, [isExpanded, autoSave]);

  // Load auto-save on component mount and check privacy notice
  useEffect(() => {
    const loadAutoSave = async () => {
      try {
        const autoSaveDraft = await draftService.getAutoSave();
        if (autoSaveDraft && !isExpanded) {
          // Don't auto-load, just indicate there's a draft available
        }
      } catch (error) {
        console.error('Failed to load auto-save:', error);
      }
    };
    loadAutoSave();

    // Check if user has seen the privacy notice when component mounts expanded
    if (isExpanded) {
      const hasSeenNotice = localStorage.getItem('hasSeenPatientPrivacyNotice');
      if (hasSeenNotice !== 'true') {
        setShowPrivacyNotice(true);
      }
    }
  }, []);

  // Check for selectedDraft from DraftsPage
  useEffect(() => {
    const selectedDraftJson = localStorage.getItem('selectedDraft');
    if (selectedDraftJson) {
      try {
        const draft: PostDraft = JSON.parse(selectedDraftJson);
        // Load the draft into the form
        handleLoadDraft(draft);
        // Clear the localStorage item
        localStorage.removeItem('selectedDraft');
      } catch (error) {
        console.error('Failed to load selected draft:', error);
      }
    }
  }, []);

  // Listen for openCreatePost event from Header
  useEffect(() => {
    const handleOpenCreatePost = () => {
      setIsExpanded(true);

      // Check if user has seen the privacy notice
      const hasSeenNotice = localStorage.getItem('hasSeenPatientPrivacyNotice');
      if (hasSeenNotice !== 'true') {
        setShowPrivacyNotice(true);
      }
    };

    window.addEventListener('openCreatePost', handleOpenCreatePost);
    return () => window.removeEventListener('openCreatePost', handleOpenCreatePost);
  }, []);

  const handleSaveDraft = async () => {
    if (!title.trim() && !description.trim()) return;

    try {
      const draftId = await draftService.saveDraft({
        title,
        description,
        selectedCategory,
        pollQuestion,
        pollOptions,
        showPoll
      });

      // Clear auto-save since we've manually saved
      await draftService.clearAutoSave();

      setCurrentDraftId(draftId);
      showSuccess('Draft saved!', 'Your draft has been saved successfully');

      // Reset form and collapse after saving
      resetForm();
    } catch (error) {
      console.error('Failed to save draft:', error);
      showError('Save failed', 'Failed to save draft. Please try again.');
    }
  };

  const handleLoadDraft = (draft: PostDraft) => {
    setTitle(draft.title);
    setDescription(draft.description);
    setSelectedCategory(draft.selectedCategory);
    setPollQuestion(draft.pollQuestion);
    setPollOptions(draft.pollOptions);
    setShowPoll(draft.showPoll);
    setCurrentDraftId(draft.id);
    setIsExpanded(true);
  };

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const videoFiles = files.filter(f => f.type.startsWith('video/'));
    const pdfFiles = files.filter(f => f.type === 'application/pdf');

    // Check if this is the first time uploading images and show privacy notice
    const hasSeenNotice = localStorage.getItem('hasSeenPatientPrivacyNotice');
    if ((imageFiles.length > 0 || pdfFiles.length > 0) && hasSeenNotice !== 'true') {
      setShowPrivacyNotice(true);
      // Store files temporarily to add after accepting notice
      e.target.setAttribute('data-pending-files', 'true');
      return;
    }

    if (imageFiles.length > 0) {
      // Add new images, limit to 5 total
      setImages(prev => [...prev, ...imageFiles].slice(0, 5));
      setPdfDocument(null);
    } else if (videoFiles.length > 0) {
      // Only take first video
      setVideo(videoFiles[0]);
      setPdfDocument(null);
    } else if (pdfFiles.length > 0) {
      // Only take first PDF
      setPdfDocument(pdfFiles[0]);
      setImages([]);
      setVideo(null);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      // Check if this is the first time uploading images and show privacy notice
      const hasSeenNotice = localStorage.getItem('hasSeenPatientPrivacyNotice');
      if (hasSeenNotice !== 'true') {
        setShowPrivacyNotice(true);
        e.target.setAttribute('data-pending-files', 'true');
        return;
      }

      const imageOnly = files.filter(f => f.type.startsWith('image/'));
      if (imageOnly.length > 0) {
        setImages(prev => [...prev, ...imageOnly].slice(0, 5));
      }
    }
  };

  // Unused handler - kept for potential future use
  // const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (file) {
  //     setVideo(file);
  //     setImages([]);
  //   }
  // };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const addPollOption = () => {
    if (pollOptions.length >= 4) {
      return;
    }
    
    const newOption: PollOption = {
      id: Date.now().toString(),
      text: ''
    };
    setPollOptions([...pollOptions, newOption]);
    
    // Focus the new option input after React renders it
    setTimeout(() => {
      const input = pollOptionRefs.current[newOption.id];
      if (input) {
        input.focus();
      }
    }, 0);
  };

  const updatePollOption = (id: string, text: string) => {
    setPollOptions(pollOptions.map(option => 
      option.id === id ? { ...option, text } : option
    ));
  };

  const removePollOption = (id: string) => {
    if (pollOptions.length > 3) {
      setPollOptions(pollOptions.filter(option => option.id !== id));
    }
  };

  // Completely hide the component for non-surgeons
  // Check privacy notice on initial render if form is being shown
  useEffect(() => {
    if ((onClose || isExpanded) && !showPrivacyNotice) {
      const hasSeenNotice = localStorage.getItem('hasSeenPatientPrivacyNotice');
      if (hasSeenNotice !== 'true') {
        setShowPrivacyNotice(true);
      }
    }
  }, [onClose, isExpanded]);

  if (!canCreatePosts) {
    return null;
  }

  // If we have onClose prop, always show the form (used by Feed's "Start a post")
  // Otherwise, respect isExpanded state (used when triggered from header + button)
  if (!onClose && !isExpanded) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b theme-border-primary">
        <h2 className="text-lg font-semibold theme-text-primary">Create New Post</h2>
        {lastAutoSave && (
          <span className="text-xs theme-text-tertiary">
            Auto-saved {formatDistanceToNow(lastAutoSave)} ago
          </span>
        )}
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Category Selection */}
        <div>
          <label className="block text-sm font-medium theme-text mb-2">
            Joint <span className="text-red-500">*</span>
          </label>
          <div className="flex space-x-2 flex-wrap">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-primary-600 text-white'
                    : 'theme-button'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Title Input */}
        <div>
          <label className="block text-sm font-medium theme-text mb-2">
            Title <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="h-10 w-10 rounded-full object-cover"
                />
              ) : (
                <span className="text-white text-sm font-medium">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              )}
            </div>
            <div className="flex-1 relative">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title..."
                className="w-full theme-input rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-600 focus:border-primary-600 text-lg font-semibold focus:bg-white dark:focus:bg-gray-600 transition-colors"
                maxLength={65}
              />
              <div className="absolute bottom-2 right-3 text-xs theme-text-muted theme-surface px-2 py-1 rounded-md">
                {title.length}/65
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-4">
          <label className="block text-sm font-medium theme-text mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your case..."
              rows={6}
              className="w-full theme-input rounded-xl p-4 focus:ring-2 focus:ring-primary-600 focus:border-primary-600 resize-none focus:bg-white dark:focus:bg-gray-600 transition-colors"
              maxLength={1100}
            />
            <div className="absolute bottom-3 right-3 text-xs theme-text-muted theme-surface px-2 py-1 rounded-md">
              {description.length}/1100
            </div>
          </div>
          
          {/* Speech-to-Text Button */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-center">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-3">Or speak your description:</p>
                <SpeechToTextButton
                  onTranscriptionUpdate={handleSpeechTranscription}
                  onError={handleSpeechError}
                  baseUrl={API_BASE_URL}
                  token={token || ''}
                  className="mx-auto"
                  useWebSpeechAPI={false}
                  useWhisperAPI={true}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Attachment Section - Show until user has filled every slot. Hidden only when a PDF is selected (PDF is exclusive) or when we already have 5 images AND a video. */}
        {!pdfDocument && !(images.length >= 5 && video) && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <label className="cursor-pointer block">
              <input
                type="file"
                accept={video ? "image/*,application/pdf" : images.length > 0 ? "video/*,application/pdf" : "image/*,video/*,application/pdf"}
                multiple
                onChange={handleAttachmentChange}
                className="hidden"
              />
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6 hover:border-primary-500 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all group">
                <div className="flex flex-col items-center justify-center space-y-3">
                  <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-700 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/20 flex items-center justify-center transition-colors">
                    <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {images.length === 0 && !video ? 'Add Attachments' : video ? 'Add Photos' : 'Add Video'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {images.length === 0 && !video ? 'Images (up to 5), videos, or PDFs' : video ? `Up to ${5 - images.length} more image${5 - images.length === 1 ? '' : 's'}` : 'A video to go with your photos'}
                    </p>
                  </div>
                </div>
              </div>
            </label>
          </div>
        )}

        {/* Media Preview - Multiple Images */}
        {images.length > 0 && (
          <div className="mb-4 grid grid-cols-2 gap-2">
            {images.map((img, index) => (
              <div key={index} className="relative">
                <img
                  src={URL.createObjectURL(img)}
                  alt={`Preview ${index + 1}`}
                  className="w-full rounded-lg h-32 object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-2 right-2 bg-gray-900 bg-opacity-75 text-white rounded-full p-1 hover:bg-opacity-90"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                {index === 0 && images.length > 1 && (
                  <div className="absolute bottom-2 left-2 bg-gray-900 bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                    1 of {images.length}
                  </div>
                )}
              </div>
            ))}
            {images.length < 5 && (
              <label className="cursor-pointer border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg h-32 flex items-center justify-center hover:border-primary-500 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                />
                <div className="text-center">
                  <svg className="w-8 h-8 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <p className="text-xs text-gray-500 mt-1">Add more</p>
                </div>
              </label>
            )}
          </div>
        )}

        {video && (
          <div className="mb-4 relative">
            <video
              src={URL.createObjectURL(video)}
              controls
              className="w-full rounded-lg max-h-64"
            />
            <button
              type="button"
              onClick={() => setVideo(null)}
              className="absolute top-2 right-2 bg-gray-900 bg-opacity-75 text-white rounded-full p-1 hover:bg-opacity-90"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* PDF Preview */}
        {pdfDocument && (
          <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <svg className="w-10 h-10 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{pdfDocument.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {(pdfDocument.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPdfDocument(null)}
                className="text-red-500 hover:text-red-600"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Poll Section */}
        {showPoll && (
          <div className="space-y-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-800 dark:text-white">Poll</h4>
              <button
                type="button"
                onClick={() => setShowPoll(false)}
                className="text-red-500 hover:text-red-600 text-sm"
              >
                Remove
              </button>
            </div>
            
            {/* Poll Question */}
            <input
              type="text"
              value={pollQuestion}
              onChange={(e) => setPollQuestion(e.target.value)}
              placeholder="Poll question..."
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            />

            {/* Poll Options */}
            <div className="space-y-2">
              {pollOptions.map((option, index) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <input
                    ref={(el) => { pollOptionRefs.current[option.id] = el; }}
                    type="text"
                    value={option.text}
                    onChange={(e) => updatePollOption(option.id, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  />
                  {pollOptions.length > 3 && (
                    <button
                      type="button"
                      onClick={() => removePollOption(option.id)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              
              {/* Add Poll Option */}
              {pollOptions.length >= 4 ? (
                <div className="w-full px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 text-center">
                    ⚠️ Maximum 4 options reached
                  </p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={addPollOption}
                  className="w-full px-3 py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-primary-600 hover:text-primary-600 transition-colors flex items-center justify-center space-x-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Add Option ({pollOptions.length}/4)</span>
                </button>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <div className="flex space-x-4">
            {/* Poll Toggle Button */}
            <button
              type="button"
              onClick={() => setShowPoll(!showPoll)}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                showPoll
                  ? 'bg-green-50 border border-primary-600 text-primary-600'
                  : 'bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span>{showPoll ? 'Remove Poll' : 'Add Poll'}</span>
            </button>
          </div>

          {/* Error Message */}
          {(errorMessage || postError) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-800">{postError || errorMessage}</p>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-2">
            {/* Auto-save indicator */}
            {lastAutoSave && (
              <span className="text-xs text-gray-400 mr-2">
                Auto-saved {new Date().getTime() - lastAutoSave.getTime() > 60000 
                  ? Math.floor((new Date().getTime() - lastAutoSave.getTime()) / 60000) + 'm ago'
                  : 'just now'}
              </span>
            )}
            
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={!title.trim() && !description.trim()}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-primary-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>Save Draft</span>
            </button>
            
            <button
              type="button"
              onClick={() => {
                resetForm();
                if (onClose) {
                  onClose();
                }
              }}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={!title.trim() || !description.trim() || isSubmitting}
              className="px-6 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>
      </form>

      {/* Patient Privacy Notice Modal */}
      <PatientPrivacyNotice
        isOpen={showPrivacyNotice}
        onAccept={() => {
          setShowPrivacyNotice(false);
          // Re-trigger file input if there were pending files
          const fileInput = document.querySelector('input[data-pending-files="true"]') as HTMLInputElement;
          if (fileInput) {
            fileInput.removeAttribute('data-pending-files');
            fileInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }}
      />
    </div>
  );
}
